import React, { useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import { ModalDialog } from 'src/components/ModalDialog';
import Switch from 'src/components/AntSwitch';
import {
  COOKIES_CONSENT_TYPES,
  DEFAULT_COOKIES_CONSENT_TYPES,
} from 'src/constants';

const ActionButton = styled(Button)(() => {
  return {
    height: 46,
    fontSize: 16,
    fontWeight: 700,
  };
});

const CookieCategory = ({ isOn, disabled, title, children, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          '.MuiSwitch-root': {
            ml: 'auto',
          },
        }}
      >
        <IconButton
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          onClick={() => {
            setIsExpanded((v) => !v);
          }}
        >
          {isExpanded && <ExpandLessIcon sx={{ marginLeft: 'auto' }} />}
          {!isExpanded && <ExpandMoreIcon sx={{ marginLeft: 'auto' }} />}
        </IconButton>
        <Typography fontWeight={600}>{title}</Typography>
        <Switch
          checked={isOn}
          disabled={disabled}
          onChange={() => onChange(!isOn)}
          inputProps={{ 'aria-label': `${title} cookies` }}
        />
      </Box>
      {isExpanded && children}
    </>
  );
};

const CookiesDetailedModal = ({ open, onClose, onAccept }) => {
  const [useNonEssential, setUseNonEssential] = useState(false);

  const handleAccept = () => {
    const types = [...DEFAULT_COOKIES_CONSENT_TYPES];
    if (useNonEssential) {
      types.push(COOKIES_CONSENT_TYPES.NON_ESSENTIAL);
    }
    onAccept(types);
  };
  return (
    <ModalDialog
      isOpen={open}
      sx={{
        '.MuiDialog-container > *': {
          overflow: 'visible',
          maxWidth: 621,
          padding: { xs: '16px 8px', md: '16px 32px' },
        },
        '.MuiDialogTitle-root': {
          mt: { xs: 4 },
          mb: { xs: 2.6, md: 2.5 },
        },
      }}
      title="Manage Cookie Preferences"
      content={
        <Box display="flex" flexDirection="column" width="100%">
          <Typography>
            We respect your privacy and believe in transparency. Below you can
            review and manage the types of cookies we use to operate our
            website.
          </Typography>
          <Box>
            <CookieCategory
              isOn={true}
              disabled={true}
              title="Strictly Necessary / Essential"
            >
              <Box
                component="ul"
                sx={{
                  pl: 3,
                  listStyle: 'disc',
                  '& li': {
                    display: 'list-item',
                  },
                  '& li b': {
                    fontWeight: 600,
                  },
                }}
              >
                <li>
                  <b>Session cookies/tokens</b> to keep users logged in
                </li>
                <li>
                  <b>Secure local storage/session storage</b> for user
                  recognition (non-tracking)
                </li>
                <li>
                  <b>Security functions</b>, including CSRF tokens, auth tokens,
                  and fraud prevention
                </li>
                <li>
                  <b>User-requested preferences</b>, such as UI settings or
                  accessibility features
                </li>
              </Box>
            </CookieCategory>
            <CookieCategory
              isOn={useNonEssential}
              title="Non-Essential"
              onChange={(v) => setUseNonEssential(v)}
            >
              <Box
                component="ul"
                sx={{
                  pl: 3,
                  listStyle: 'disc',
                  '& li': {
                    display: 'list-item',
                  },
                  '& li b': {
                    fontWeight: 600,
                  },
                }}
              >
                <li>
                  <b>Analytics tools</b> (e.g., Google Analytics, Mixpanel,
                  Hotjar)
                </li>
                <li>
                  <b>Advertising and retargeting cookies</b>
                </li>
                <li>
                  <b>Social media tracking pixels</b> (e.g., Facebook Pixel,
                  LinkedIn Insight Tag)
                </li>
                <li>
                  <b>Pixel tags / web beacons</b>
                </li>
                <li>
                  <b>Cross-site or cross-device tracking technologies</b>
                </li>
                <li>
                  <b>Device fingerprinting</b>
                </li>
                <li>
                  <b>A/B testing platforms</b>
                </li>
                <li>
                  <b>Behavioral profiling tools</b>
                </li>
              </Box>
            </CookieCategory>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              mt: 2,
              pr: 1,
              pl: 1,
            }}
          >
            <ActionButton
              variant="contained"
              type="submit"
              onClick={handleAccept}
            >
              Accept
            </ActionButton>
          </Box>
        </Box>
      }
      onClose={onClose}
    />
  );
};
export default CookiesDetailedModal;
